import { useState } from "react";
import { App, Alert, Button, ConfigProvider, Form, Input, Space, theme, Typography } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { post, session } from "./api";
import type { Agent } from "./domain";
export function Login({ onLogin }: { onLogin: (agent: Agent) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <ConfigProvider theme={{
      algorithm: theme.darkAlgorithm,
      token: {
        colorPrimary: "#2b66f5",
        colorText: "#f2f6ff",
        colorTextSecondary: "#afc3de",
        colorTextPlaceholder: "#8fa8c7",
        colorBgContainer: "#132b46",
        colorBorder: "#3b5b80",
        colorError: "#ff8f99",
        controlHeightLG: 50,
        borderRadius: 12,
      },
      components: {
        Button: { colorPrimary: "#2863f2", colorPrimaryHover: "#467dff", colorPrimaryActive: "#2054d0", primaryShadow: "0 8px 24px #061b4133" },
        Input: { activeBg: "#152f4d", hoverBg: "#152f4d", activeBorderColor: "#6b9cff", hoverBorderColor: "#6b9cff", activeShadow: "0 0 0 3px #528cff26" },
      },
    }}>
    <div className="auth-page">
      <div className="auth-story">
        <div className="brand-lockup">
          <img className="brand-mark" src="/brand-icon.png" alt="体验官 Logo" />
          <strong>
            体验官<span>MERCHANT WORKSPACE</span>
          </strong>
        </div>
        <div className="auth-headline">
          <div className="eyebrow">让每一次体验，都有价值</div>
          <h1>
            专注任务。
            <br />
            看见每一份成果。
          </h1>
          <p>
            从任务创建到执行洞察，
            <br />
            在一个清晰、安心的工作空间里完成。
          </p>
          <div className="auth-pills">
            <span>
              <CheckCircleFilled /> 独立业务空间
            </span>
            <span>
              <CheckCircleFilled /> 真实在线报表
            </span>
            <span>
              <CheckCircleFilled /> 隐私安全保护
            </span>
          </div>
        </div>
        <div className="auth-footnote">体验官商户协作平台</div>
      </div>
        <div className="auth-decoration" aria-hidden="true">
          <div />
          <div />
          <div />
          <div />
          <div />
        </div>
      <div className="auth-form-side">
        <div className="auth-mobile-brand">
          <img className="brand-mark" src="/brand-icon.png" alt="" />
          <span>体验官 · 商户平台</span>
        </div>
        <div className="auth-card">
          <Tagline />
          <h2>欢迎回来</h2>
          <p className="muted">登录您的商户平台</p>
          {error && (
            <Alert
              showIcon
              type="error"
              message={error}
              style={{ marginBottom: 24 }}
            />
          )}
          <Form
            layout="vertical"
            size="large"
            onFinish={async (values: {
              username: string;
              password: string;
            }) => {
              setBusy(true);
              setError("");
              try {
                const result = await post<{
                  access_token: string;
                  agent: Agent;
                }>("/auth/login", values);
                session.set(result.access_token);
                onLogin(result.agent);
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Form.Item
              label="登录账号"
              name="username"
              rules={[{ required: true, message: "请输入商户账号" }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="请输入账号"
                autoComplete="username"
                maxLength={100}
              />
            </Form.Item>
            <Form.Item
              label="登录密码"
              name="password"
              rules={[{ required: true, message: "请输入密码" }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入密码"
                autoComplete="current-password"
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={busy}
              icon={<ArrowRightOutlined />}
              iconPosition="end"
            >
              登录商户平台
            </Button>
          </Form>
          <div className="login-help">
            <SafetyCertificateOutlined /> 使用独立商户账号，账号由总后台开通
          </div>
          <div className="auth-contact">
            忘记密码或账号受限？请联系平台管理员。
          </div>
        </div>
        <div className="auth-copyright">
          © {new Date().getFullYear()} 体验官 · 安全连接 · Asia/Shanghai
        </div>
      </div>
    </div>
    </ConfigProvider>
  );
}
function Tagline() {
  return (
    <div className="login-label">
      <span /> 商户专属入口
    </div>
  );
}
export function PasswordForm({
  forced = false,
  onDone,
}: {
  forced?: boolean;
  onDone: () => void;
}) {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <div className={forced ? "password-page" : ""}>
      <div className="password-card">
        {forced && (
          <>
            <div className="password-icon">
              <SafetyCertificateOutlined />
            </div>
            <h2>设置您的专属密码</h2>
            <p className="muted">
              首次登录需修改初始密码，完成后即可进入首页。
            </p>
          </>
        )}
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 20 }}
          />
        )}
        <Form
          layout="vertical"
          onFinish={async (v: {
            current_password: string;
            new_password: string;
          }) => {
            setBusy(true);
            setError("");
            try {
              await post("/auth/password", {
                current_password: v.current_password,
                new_password: v.new_password,
              });
              session.clear();
              message.success("密码已修改，请使用新密码重新登录");
              onDone();
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Form.Item
            label="当前密码"
            name="current_password"
            rules={[{ required: true, message: "请输入当前密码" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="new_password"
            extra="至少 12 位，包含大写字母、小写字母、数字和符号。"
            rules={[
              { required: true, message: "请输入新密码" },
              { min: 12, message: "新密码至少需要 12 位" },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
                message: "请同时包含大小写字母、数字和符号",
              },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || value !== getFieldValue("current_password")
                    ? Promise.resolve()
                    : Promise.reject(new Error("新密码不能与当前密码相同")),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirm_password"
            dependencies={["new_password"]}
            rules={[
              { required: true, message: "请再次输入新密码" },
              ({ getFieldValue }) => ({
                validator: (_, v) =>
                  !v || getFieldValue("new_password") === v
                    ? Promise.resolve()
                    : Promise.reject(new Error("两次输入的密码不一致")),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={busy}>
              保存并重新登录
            </Button>
            {forced && (
              <Button
                onClick={() => {
                  session.clear();
                  onDone();
                }}
              >
                退出登录
              </Button>
            )}
          </Space>
        </Form>
        {!forced && (
          <Typography.Paragraph type="secondary" style={{ marginTop: 20 }}>
            修改密码后，所有设备的登录状态将失效。
          </Typography.Paragraph>
        )}
      </div>
    </div>
  );
}
